import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
// 휴대폰 전용 메타데이터. 유선(02xxxxxxxx) / 인터넷전화(070) / 대표번호(1588) 는 유효하지 않다.
import { isValidPhoneNumber as isValidMobileNumber } from "libphonenumber-js/mobile";
import moment from "moment";
import numeral from "numeral";
import { ProductListModel, ProductModel } from "src/api/product/product-types";
import i18n from "src/i18n";

export function getUserDevice() {
  const varUA = navigator.userAgent.toLowerCase(); // userAgent 값 얻기
  if (varUA.indexOf("android") > -1) {
    // 안드로이드
    return "android";
  } else if (varUA.indexOf("iphone") > -1 || varUA.indexOf("ipad") > -1 || varUA.indexOf("ipod") > -1 || varUA.indexOf("ios") > -1) {
    // IOS
    return "ios";
  } else {
    // IOS, 안드로이드 외 (PC)
    return "other";
  }
}

/* 
  number 를 받아서 천단위 콤마를 찍어서 string 으로 리턴
  ex) numberToStringWithComma(1000) => "1,000"
  ex) numberToStringWithComma(1000.34) => "1,000"
  ex) numberToStringWithComma(1000.34, "0,0.00") => "1,000.34"
  ex) numberToStringWithComma(1000.34, "0,0.0") => "1,000.3"
*/
export function numberToStringWithComma(num?: number, format: string = "0,0"): string {
  if (!num) return "0";
  if (Math.floor(num) === num) {
    // 정수
    return numeral(num).format(format);
  }
  // 소수까지
  const [intVal, decimalVal] = String(num).split(".");
  return numeral(intVal).format("0,0") + "." + decimalVal;
}

/* 
  소수점 두자리까지 반올림
  ex) roundToTwo(45.666) => 45.67
*/
export function roundToTwo(num?: number): number {
  const value = num || 0;
  const m = Number((Math.abs(value) * 100).toPrecision(15));
  const round = (Math.round(m) / 100) * Math.sign(value);
  return round;
}

/* 
  소수점 두자리까지 버림
  ex) floorToTwo(45.666) => 45.66
*/
export function floorToTwo(num?: number): number {
  const value = num || 0;
  const m = Number((Math.abs(value) * 100).toPrecision(15));
  const floor = (Math.floor(m) / 100) * Math.sign(value);
  return floor;
}

/* 
  평수 계산 (m2 x 0.3025, 소수점 2자리 반올림). 천단위 콤마 처리
  ex) calculatePyeong(1000000) => "302,500.00"
  ex) calculatePyeong(1000000, "0,0") => "302,500"
 */
export function calculatePyeong(num?: number, format: string = "0,0.00"): string {
  const pyeong = (num || 0) * 0.3025;
  return numeral(pyeong).format(format);
}

/* 
  평수 계산 (m2 x 0.3025, 소수점 2자리 반올림)
  ex) calculatePyeongNum(444444) => 134444.31
 */
export function calculatePyeongNum(num?: number): number {
  const pyeong = (num || 0) * 0.3025;
  return roundToTwo(pyeong);
}

// 전용률 구하기 - 전용면적 / 계약면적 * 100
export function calculateRateOfUse(
  leasableAreaNet: string | number | undefined,
  leasableArea: string | number | undefined,
  format: string = "0,0.00",
): string {
  const value = (Number(leasableAreaNet || 0) / Number(leasableArea || 0)) * 100;
  const floorValue = floorToTwo(value);
  const result = numeral(floorValue).format(format);
  return result;
}

// 비용 단위 천 / 억 으로 변환
export const getKoreanCost = (cost?: number | string) => {
  // if (!cost) return ""; // 주석처리한이유:: 0도 유의미한값이므로 출력해야함
  const koreanUnits = ["원", "만원", "억", "조"];
  let answer = "";
  let unit = 10000;
  let index = 0;
  let division = Math.pow(unit, index);

  const costNumber = Number(cost || 0);

  while (Math.floor(costNumber / division) > 0) {
    const mod = Math.floor((costNumber % (division * unit)) / division);
    if (mod) {
      const modToString = mod.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      answer = `${modToString}${koreanUnits[index]} ` + answer;
    }
    // division = Math.pow(unit, ++index);
    division = Math.pow(unit, ++index);
  }
  return `${answer || 0}`;
};

// 1억 5500만원 => cost, unit 으로 분리
type CostUnit = {
  cost?: string; // 1억 5500
  unit?: "원" | "만원" | undefined; // 만원
};
export function getKoreanCostUnits(cost?: number | string): CostUnit {
  const defaultCostUnit: CostUnit = { cost: undefined, unit: "원" };
  if (cost === null || undefined) return defaultCostUnit;
  const costNumber = Number(cost || 0);
  const koreanCode = getKoreanCost(costNumber);

  if (koreanCode.lastIndexOf("만원") > -1) {
    return {
      cost: koreanCode.substring(0, koreanCode.lastIndexOf("만원")),
      unit: "만원",
    };
  } else if (koreanCode.lastIndexOf("원") > -1) {
    return {
      cost: koreanCode.substring(0, koreanCode.lastIndexOf("원")),
      unit: "원",
    };
  } else {
    return {
      cost: koreanCode,
      unit: "원",
    };
  }
}

/**
 * 원본 이미지 url -> resized 이미지 url 로 변환
 */
export function getResizedImageUrl(url?: string): string {
  if (!url) return "";
  const extIndex = url.lastIndexOf(".");
  const ext = url.slice(extIndex); // 파일 확장자
  const resizedImageUrl = url.slice(0, extIndex) + "_resized" + ext;
  return resizedImageUrl;
}
/**
 * 원본 이미지 url -> resized_small 이미지 url 로 변환
 */
export function getResizedSmallImageUrl(url?: string): string {
  if (!url) return "";
  const extIndex = url.lastIndexOf(".");
  const ext = url.slice(extIndex); // 파일 확장자
  const resizedImageUrl = url.slice(0, extIndex) + "_resized_small" + ext;
  return resizedImageUrl;
}

/**
 * 시작시간 ~ 종료시간 차이
 * @param startDate
 * @param endDate
 * @returns "1시간 30분", "1시간", "30분", ""
 */
export function getTimeDiff(startDate?: string, endDate?: string): string {
  if (!startDate || !endDate) return "";
  const diff = moment(startDate).diff(moment(endDate));
  const duration = moment.duration(diff);
  const diffHours = Math.floor(Math.abs(duration.asHours())); // asHours 24시간 이상도 커버하는 시간차
  const diffMinutes = Math.floor(Math.abs(duration.minutes())); // minutes 분 차이

  if (diffHours === 0 && diffMinutes === 0) return "";

  let result = "";
  if (diffHours > 0) {
    result += i18n.t("detail.reservationDurationHour", { count: diffHours, ns: "contract" });
  }
  if (diffMinutes > 0) {
    result += (diffHours > 0 ? " " : "") + i18n.t("detail.reservationDurationMinute", { count: diffMinutes, ns: "contract" });
  }
  return result;
}

/**
 * 이용시작시간 ~ 이용종료시간 차이 괄호 포함 eg."(1시간 30분)"
 * @param useStartDate
 * @param useEndDate
 * @returns "(1시간 30분)", ""
 */
export function getUseTimeDiff(useStartDate?: string, useEndDate?: string): string {
  if (!useStartDate || !useEndDate) return "";

  const diff = getTimeDiff(useStartDate, useEndDate);
  return diff ? `(${diff})` : "";
}

/**
 * 종료 시각 HH:mm 표기. 자정으로 끝나는 구간은 다음날 00:00 이 아닌 24:00 으로 보여준다. (QA-3824)
 * @param endDate 구간 종료 일시
 * @returns "24:00", "23:30", ""
 */
export function formatEndTime(endDate?: string | moment.Moment): string {
  if (!endDate) return "";
  const time = moment(endDate).format(ViewYmdFormat.HH_MM);
  return time === "00:00" ? "24:00" : time;
}

/**
 * 휴대폰 번호 validation ("+821012345678" || "01012345678" || "010-1234-5678")
 *
 * 자릿수만 맞는 번호가 FE 검증을 통과하고 BE 에서 거부되는 문제가 있어 정규식 대신
 * libphonenumber-js 로 검증한다. (QA-3843)
 * - BE 자바 libphonenumber v9.0.40 과 룰을 맞추려고 libphonenumber-js 를 1.13.14 로 고정했다.
 *   버전마다 룰이 달라 캐럿(^)을 쓰면 BE 와 판정이 어긋난다. (QA-3840 참고)
 * - 이 버전에서 "010-0xxx-xxxx" 는 유효한 번호이므로 BE 와 함께 허용한다.
 * - 정규식과 달리 하이픈 입력, 011 등 구 식별번호, 10자리 010 번호도 통과한다. BE 와 같은
 *   판정을 쓰는 것이 목적이므로 FE 에서 따로 더 좁히지 않는다.
 *
 * @param phoneNumber 입력한 휴대폰 번호
 * @param requiredMessage 필수입력 에러 메세지 (없으면 "휴대폰 번호를 입력해주세요")
 * @param formatMessage 휴대폰 번호 포맷 에러 메세지 (없으면 "올바르지 않은 휴대폰 번호입니다")
 * @returns
 */
export function validatePhoneNumber(phoneNumber?: string, requiredMessage?: string, formatMessage?: string): boolean | string {
  if (!phoneNumber) return requiredMessage || "휴대폰 번호를 입력해주세요";
  return isValidMobileNumber(phoneNumber, "KR") || formatMessage || "올바르지 않은 휴대폰 번호입니다";
}

/**
 * 휴대폰 번호 validation 의 boolean 버전. 판정 기준은 validatePhoneNumber 와 같다.
 */
export function validatePhoneNumberBoolean(phoneNumber?: string): boolean {
  if (!phoneNumber) return false;
  return isValidMobileNumber(phoneNumber, "KR");
}

/**
 * international 휴대폰번호 형식을 일반적인 한국 표기 방식으로 변환
 * @param phoneNumber "+821012345678", "01012345678"
 * @returns "010-1234-5678"
 */
export function convertKoreanPhoneNumber(phoneNumber?: string): string {
  if (!phoneNumber) return "";
  let result = phoneNumber.replace(/ /g, ""); // 공백제거
  result = result.split("-").join(""); // 데시제거

  if (result.startsWith("+820")) {
    result = result.replace("+820", "0");
  } else if (result.startsWith("+82")) {
    result = result.replace("+82", "0");
  }
  if (result.length >= 11) {
    result = [result.substring(0, 3), result.substring(3, 7), result.substring(7, 11)].join("-");
  } else {
    result = [result.substring(0, 3), result.substring(3, 6), result.substring(6)].join("-");
  }
  return result;
}

/**
 * +, -, 숫자, 공백 포함한 숫자 제외하고는 replace
 * @param str
 * @returns
 */
export function replaceNumberWithPlusMinus(str: string) {
  return str.replace(/[^\d+-\s]+/g, "");
}

// webview에서 페이지 이동을 할 때 사용
export function navigateWebviewPage(path: string) {
  const a = document.createElement("a");
  a.href = path;
  a.dispatchEvent(new MouseEvent("click", { bubbles: true }));
}

export function sanitizePhoneOrEmailSearch(str: string) {
  return str.replace(/[^a-zA-Z0-9@._+\-\s]/g, "");
}

// 숫자만 입력
export function onlyNumber(value: string) {
  return value.replace(/[^0-9]/g, "");
}

// 콤마 삭제
export const deleteCommas = (str: any) => {
  if (str) {
    if (typeof str === "number") {
      return;
    } else {
      if (str !== "") {
        return str.replace(/,/g, "");
      } else {
        return;
      }
    }
  } else {
    return;
  }
};

/** 분 number 입력시 "x시간" or "x시간x분" 으로 리턴해주는 함수 */
export const makeTimeString = (value: number) => {
  const hour = Math.floor(value / 60);
  const minute = Math.floor(value % 60);

  if (value < 60) {
    return `${value}분`;
  }

  if (minute === 0) {
    return `${hour}시간`;
  }

  return `${hour}시간 ${minute}분`;
};

export const onChangeKoreanDays = (date?: string) => {
  if (date) {
    const day = moment(date).format("dddd");
    if (day) {
      let koreanDay = "";
      switch (day) {
        case "월요일":
          koreanDay = "월";
          break;
        case "화요일":
          koreanDay = "화";
          break;
        case "수요일":
          koreanDay = "수";
          break;
        case "목요일":
          koreanDay = "목";
          break;
        case "금요일":
          koreanDay = "금";
          break;
        case "토요일":
          koreanDay = "토";
          break;
        case "일요일":
          koreanDay = "일";
          break;
      }
      return koreanDay;
    }
  }
};
export const makeCommonSpaceList = (product: any) => {
  const tableData: any = [];

  if (product.isMeetingRoom) {
    product?.buildingList?.forEach((data: any) => {
      data.buildingCommonFacility?.meetingRoomList?.forEach((space: any) => {
        let returnDt = { ...space, buildingName: data.buildingName, isSelected: false, isSelectedId: "isSelected_" + space.id };
        tableData.push(returnDt);
      });
    });
  }

  if (product.isRefreshRoom) {
    product?.buildingList?.forEach((data: any) => {
      data.buildingCommonFacility?.refreshRoomList?.forEach((space: any) => {
        let returnDt = { ...space, buildingName: data.buildingName, isSelected: false, isSelectedId: "isSelected_" + space.id };
        tableData.push(returnDt);
      });
    });
  }

  if (product.isDesk) {
    product?.buildingList?.forEach((data: any) => {
      data.buildingCommonFacility?.deskSpace?.deskGroupList?.forEach((deskGroup: any) =>
        deskGroup.deskList?.forEach((desk: any) => {
          let returnDt = {
            ...desk,
            groupName: deskGroup.groupName,
            buildingName: data.buildingName,
            isSelected: false,
            isSelectedId: "isSelected_" + desk.id,
          };
          tableData.push(returnDt);
        }),
      );
    });
  }

  return tableData;
};

const isPrimitiveArray = (value: unknown): value is (string | number)[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string" || typeof item === "number");

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && Object.getPrototypeOf(value) === Object.prototype;

/**
 * 쿼리 파라미터의 문자열·숫자 배열을 콤마 결합 문자열로 바꾼다. 중첩된 일반 객체도 재귀 처리한다.
 * 배열째 넘기면 qs 가 `statusCode[0]=` 형태로 직렬화하므로, 콤마 목록을 받는 API 에 쓴다.
 * 빈 배열은 undefined 가 되어 qs 가 키 자체를 뺀다.
 *
 * 객체 배열(`sort.orders` 등)과 Date·moment 같은 비-일반 객체는 건드리지 않는다.
 * 배열을 그대로 받는 API(`direction: ["DESC"]` 등)에는 쓰면 안 된다.
 */
export const toQueryParams = <T extends object>(params: T): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      if (isPrimitiveArray(value)) {
        return [key, value.length ? value.join(",") : undefined];
      }
      if (isPlainObject(value)) {
        return [key, toQueryParams(value)];
      }
      return [key, value];
    }),
  );

// api용으로 사용합니다.
export const YmdFormat = {
  YYYY: "YYYY",
  MM: "MM",
  YYYY_MM: "YYYY-MM",
  YYYY_MM_DD: "YYYY-MM-DD",
  TIME: "HH:mm:ss",
  YYYY_MM_DD_HH_MM: "YYYY-MM-DD HH:mm",
  FULL: "YYYY-MM-DD HH:mm:ss",
  FULL_KO: "YYYY년 MM월 DD일 HH시 mm분",
  FULL_KO1: "YYYY/MM/DD HH:mm",
  WITH_TIME_ZONE: "YYYY-MM-DDTHH:mm:ss.SSSZ",
} as const;

/** "종료일 없음" 센티넬. 기간 검색 API 의 종료 시각에 넣어 끝을 열어둔다 */
export const NO_END_TIME = "9999-12-31T23:59:59.999+09:00";

export const ViewYmdFormat = {
  YYYY: "YYYY",
  MM: "MM",
  YYYY_MM: "YYYY-MM",
  YYYY_MM_DD: "YYYY-MM-DD",
  HH_MM: "HH:mm",
  TIME: "HH:mm:ss",
  YYYY_MM_DD_HH_MM: "YYYY-MM-DD HH:mm",
  FULL: "YYYY-MM-DD HH:mm:ss",
  YYYY_MM_DD_WEEK: "YYYY-MM-DD(ddd)",
  YYYY_MM_DD_WEEK_HH_MM: "YYYY-MM-DD(ddd) HH:mm",
} as const;

// 이메일 @ 앞에만 남기기 id만
export const getEmailNickname = (email: string) => {
  // 정규식을 사용하여 이메일 주소에서 사용자 이름을 추출합니다.
  const pattern = /^[^@]+/;
  const match = email.match(pattern);
  if (match) {
    return match[0];
  } else {
    return null;
  }
};

// 핸드폰번호 +82 E.164 포맷으로 변경
export const parsedPhoneNumber = (phone: string) => {
  let phoneNumber;
  if (phone) {
    if (isValidPhoneNumber(phone, "KR") === false) {
      return phone;
    }
    phoneNumber = parsePhoneNumber(phone, "KR").number;
  }
  return phoneNumber;
};

// E.164 포맷 -> 일반 핸드폰 포맷으로 변경 ex)010-1111-2222
export const formatPhoneNumber = (phone: string) => {
  let phoneNumber;
  if (phone) {
    if (isValidPhoneNumber(phone, "KR") === false) {
      return phone;
    }
    phoneNumber = parsePhoneNumber(phone, "KR").formatNational();
  }
  return phoneNumber;
};

// 검색용 전화번호로 변경
export const getReplacedSearchText = (searchText?: string) => {
  if (!searchText) return "";
  let searchValue = searchText.replace(/\s/g, "");
  if (/^[\d+-]+$/.test(searchValue)) {
    searchValue = searchValue.replace(/-/g, "");
    searchValue = searchValue.startsWith("010") ? searchValue.slice(1) : searchValue;
  }
  return searchValue;
};

export const linePhoneFormat = (phone: string) => {
  let phoneNumber;
  const format = /^(0(2|3[1-3]|4[1-4]|5[1-5]|6[1-4]))(\d{3,4})(\d{4})$/;
  if (phone) {
    phoneNumber = format.test(phone);
  }
  return phoneNumber;
};

export const emailFormat = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // 이메일 주소 내에 한글이 포함되지 않았는지 확인
  return emailRegex.test(email);
};

/**
 * URL을 파트너 형식으로 변환하는 함수
 * @param pathname 현재 경로 (예: "/court/partner/abc/main" 또는 "/court/main")
 * @param targetUrl 변환할 URL (예: "/court/login")
 * @returns 파트너 코드가 있으면 파트너 형식의 URL, 없으면 원본 URL
 */

export const convertToPartnerUrl = (pathname: string, to: string): string => {
  const pathnameArray = pathname.split("/");

  //배열로 만들고 배열안에 "partner"가 있는지 확인
  const partnerIndex = pathnameArray.findIndex((item) => item === "partner");
  //partner가 있으면 그 뒤에 있는 값을 가져오고 없으면 빈 문자열 반환
  const partnerCode = partnerIndex !== -1 ? pathnameArray[partnerIndex + 1] : "";

  // to = http://localhost:3100/front/court/main
  // newto = http://localhost:3100/front/court/partner/${partnerCode}/main

  if (partnerCode) {
    // newto 는 to 를 배열로 만들고 "court" 뒤에 partner/${partnerCode} 를 삽입해줘
    const newToArr = to.split("/");
    newToArr.splice(2, 0, `partner/${partnerCode}`);
    const newTo = newToArr.join("/");

    return newTo;
  }

  return to;
};

// 판매 기간 메시지 생성 함수
export const getSalePeriodMessage = (product: ProductListModel | ProductModel) => {
  // 판매시작날짜가 있으면 > 시작 날짜가 오늘보다 이전일 때 판매 가능
  // 판매종료날짜가 있으면 > 종료 날짜와 시간이 현재보다 이후일 때 종료
  // 판매시작 / 판매 종료 데이터가 없으면 상시판매

  let isAvailableSaleStartDate = true; // 판매 시작 가능 여부
  let isAvailableSaleEndDate = true; // 판매 종료 가능 여부
  const saleStartDate = product.saleStartDate || null; // 판매 시작 날짜
  const saleEndDate = product.saleEndDate || null; // 판매 종료 날짜
  const salePeriodType = product.salePeriodType || "ALWAYS"; // 판매 기간 타입 (상시, 기간)

  let message = ""; // 판매 기간 메시지
  let title = ""; // 판매 기간 타이틀

  if (salePeriodType === "LIMITED") {
    // 판매 시작 날짜가 있고 현재가 시작일보다 이전이면 false
    if (saleStartDate && moment().isBefore(moment(saleStartDate))) {
      isAvailableSaleStartDate = false; // 현재가 시작일보다 이전이면 false
    }
    if (saleEndDate && moment().isAfter(moment(saleEndDate))) {
      isAvailableSaleEndDate = false; // 현재가 종료일보다 이후면 false
    }

    if (!isAvailableSaleStartDate && !isAvailableSaleEndDate) {
      title = "오픈 예정";
    } else if (!isAvailableSaleStartDate && isAvailableSaleEndDate) {
      title = "오픈 예정";
    } else if (isAvailableSaleStartDate && !isAvailableSaleEndDate) {
      title = "판매 마감";
    }

    if (saleStartDate && !saleEndDate && !isAvailableSaleStartDate) {
      message = `오픈일시 : ${moment(saleStartDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM)}`;
    }

    if (!saleStartDate && saleEndDate && !isAvailableSaleEndDate) {
      message = `마감일시 : ${moment(saleEndDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM)}`;
    }
    if (saleStartDate && saleEndDate && (!isAvailableSaleStartDate || !isAvailableSaleEndDate)) {
      message = `오픈일시 : ${moment(saleStartDate).format(ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM)}\n마감일시 : ${moment(saleEndDate).format(
        ViewYmdFormat.YYYY_MM_DD_WEEK_HH_MM,
      )}`;
    }
  }
  return { title, message, isAvailableSaleStartDate, isAvailableSaleEndDate };
};

// 장기예약 날짜표기형식
export const formatDateTimeRange = (start: string, end: string, type: "F2" | "S2" | "S2_MAIN") => {
  const startDateTime = moment(start);
  const endDateTime = moment(end);

  const dateFormat = ViewYmdFormat.YYYY_MM_DD;
  const dayOfWeekFormat = "(ddd)";
  const timeFormat = "HH:mm";

  // 현재 언어에 맞춰 요일(ddd)을 로케일화 (en → "(Wed)", ko → "(수)")
  const weekdayLocale = i18n.language?.startsWith("en") ? "en" : "ko";

  const startDate = startDateTime.format(dateFormat);
  const startDayOfWeek = startDateTime.locale(weekdayLocale).format(dayOfWeekFormat);
  const startTime = startDateTime.format(timeFormat);

  const endDate = endDateTime.format(dateFormat);
  const endDayOfWeek = endDateTime.locale(weekdayLocale).format(dayOfWeekFormat);
  const endTime = endDateTime.format(timeFormat);

  const durationMinutes = endDateTime.diff(startDateTime, "minutes");
  const durationDays = Math.floor(durationMinutes / (24 * 60));
  const remainingMinutes = durationMinutes % (24 * 60);
  const durationHours = Math.floor(remainingMinutes / 60);
  const finalMinutes = remainingMinutes % 60;

  let durationText = "";
  if (durationDays > 0) {
    durationText += `${i18n.t("detail.reservationDurationDay", { count: durationDays, ns: "contract" })} `;
  }
  if (durationHours > 0) {
    durationText += `${i18n.t("detail.reservationDurationHour", { count: durationHours, ns: "contract" })} `;
  }
  if (finalMinutes > 0) {
    durationText += `${i18n.t("detail.reservationDurationMinute", { count: finalMinutes, ns: "contract" })}`;
  }

  const isSameDay = startDateTime.format("YYYY-MM-DD") === endDateTime.format("YYYY-MM-DD");

  if (isSameDay) {
    if (type === "F2") {
      return `${startDate}${startDayOfWeek}`;
    }
    // S2_MAIN: 마이페이지 메인 카드는 `~` 뒤에서 줄바꿈하고 소요시간을 종료시각 뒤에 붙여 항상 2줄로 표기 (MVDV-9755)
    if (type === "S2_MAIN") {
      return `${startDate}${startDayOfWeek} ${startTime} ~\n${endTime} (${durationText.trim()})`;
    }
    return `${startDate}${startDayOfWeek} ${startTime} ~ ${endTime} \n(${durationText.trim()})`;
  } else {
    if (type === "F2") {
      return `${startDate}${startDayOfWeek} ~ ${endDate}${endDayOfWeek}`;
    }
    if (type === "S2_MAIN") {
      return `${startDate}${startDayOfWeek} ${startTime} ~\n${endDate}${endDayOfWeek} ${endTime} (${durationText.trim()})`;
    }
    return `${startDate}${startDayOfWeek} ${startTime} ~ ${endDate}${endDayOfWeek} ${endTime} \n(${durationText.trim()})`;
  }
};

// 분 MM > HH 시간 MM 분 포맷으로 변경
export const minutesToDisplayText = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins}분`;
  } else if (mins === 0) {
    return `${hours}시간`;
  } else if (hours < 10) {
    return `${hours}시간 ${mins}분`;
  } else {
    return `${hours.toString().padStart(2, "0")}시간 ${mins.toString().padStart(2, "0")}분`;
  }
};
